import { supabase, krevInnlogging } from "./supabase";
import type { Oekt, Rolle } from "./session";
import { velgLogo } from "@infoflyt/shared/logo";
import { velgProduksjon } from "./konsoll";

/**
 * Innlogging på nettflata.
 *
 * ========  INGEN PASSORD, OG INGEN GOOGLE  ========
 *
 * En engangskode på e-post, og ikke noe annet. Valget er tatt av
 * personvernhensyn og står begrunnet i `docs/09-juridisk.md` §
 * «Særlig om innloggingen»:
 *
 * * Et passord er en hemmelighet vi må oppbevare, og som folk gjenbruker.
 *   Uten passord finnes ingenting å lekke.
 * * En ekstern identitetsleverandør ville lært NÅR og HVOR OFTE crewet
 *   logger på en produksjon, utløst en overføringsvurdering etter kap. V,
 *   og brutt setningen «Supabase er eneste underdatabehandler».
 *
 * Det innloggingen samler inn er e-postadressen. Navn og forkortelse ligger
 * på `production_member`, fordi loggen må kunne vise hvem som skrev en post.
 *
 * ========  MEDLEMSKAPET AVGJØR, IKKE KONTOEN  ========
 *
 * Å være innlogget er ikke å ha tilgang. Rollen hører til produksjonen, ikke
 * til brukeren — samme person er regi ett sted og klipper et annet — så
 * økten bygges av `production_member`, aldri av noe på kontoen.
 *
 * Har en innlogget bruker ingen medlemskap, er hen ikke med i noen
 * produksjon ennå. Det er en normal tilstand og ikke en feil: kontoen
 * opprettes første gang man går inn i en produksjon, og flata sier det.
 */

export type Tilstand = "sjekker" | "ute" | "inne";

/** Er noen logget inn nå? Uten krav om innlogging er svaret alltid ja. */
export async function erInne(): Promise<boolean> {
  if (!krevInnlogging || supabase === null) return true;
  const { data } = await supabase.auth.getSession();
  return data.session !== null;
}

/*
 * «Finnes ikke» ER IKKE EN FEIL VI VISER.
 *
 * Med selvregistrering av svarer serveren 422 `otp_disabled` paa en adresse
 * som ikke har konto — «Signups not allowed for otp». Viste vi den, hadde
 * flata fortalt en fremmed hvem som jobber paa produksjonen, og hele poenget
 * med det felles svaret var borte.
 *
 * Derfor behandles de to kodene som suksess: brukeren kommer til kodefeltet,
 * og der feiler en kode som ikke finnes paa vanlig maate. Verifisert mot det
 * levende prosjektet 26.10.2026.
 */
const TAUSE = new Set(["otp_disabled", "signup_disabled", "email_not_confirmed"]);

function erTaus(kode: string | undefined): boolean {
  return kode !== undefined && TAUSE.has(kode);
}

/**
 * Sender engangskoden.
 *
 * KODE, IKKE LENKE — og det er ikke et valg nettflata gjoer for seg selv.
 * E-postmalen er felles (`supabase/templates/magic_link.html`), og den sender
 * kode fordi en lenke paa iOS alltid aapner i standardnettleseren, mens en PWA
 * fra hjemskjermen har eget lager. Oekten ville landet i Safari, ikke i appen.
 *
 * Her gir koden en fordel til: det ligger aldri et token i adressefeltet, og
 * dermed ingenting i historikken paa en maskin som deles i et kontrollrom.
 *
 * `shouldCreateUser: false` er ikke en detalj. Kontoen opprettes FOERSTE GANG
 * MAN GAAR INN I EN PRODUKSJON — med QR-kode, se `docs/02-arkitektur.md` —
 * ikke ved at noen skriver adressen sin i et felt. Uten dette ville den
 * offentlige anon-noekkelen vaert nok til aa opprette kontoer i kundens system.
 *
 * Konsekvensen er at en ukjent adresse faar «vi sendte en kode» uten at noe
 * ble sendt. Det er med vilje: svarer flata «den adressen finnes ikke», har
 * den nettopp fortalt en fremmed hvem som jobber paa produksjonen.
 */
export async function sendKode(epost: string): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };
  const { error } = await supabase.auth.signInWithOtp({
    email: epost.trim(),
    options: { shouldCreateUser: false },
  });
  if (error === null || erTaus(error.code)) return { ok: true };
  return { ok: false, feil: error.message };
}

/**
 * Loeser koden inn i en oekt.
 *
 * `type: "email"` og ikke `"magiclink"`: den foerste godtar koden fra begge
 * slags e-poster, den andre bare fra en innloggingslenke. Med én felles mal
 * er det den foerste vi vil ha.
 *
 * En feil her er ekte og skal VISES — i motsetning til en ukjent adresse over.
 * Koden er noe brukeren har skrevet selv, og «feil kode» forteller ingen noe
 * de ikke alt visste.
 */
export async function bekreftKode(
  epost: string, kode: string,
): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };
  const { error } = await supabase.auth.verifyOtp({
    email: epost.trim(),
    token: kode.replace(/\s/g, ""),
    type: "email",
  });
  return error === null ? { ok: true } : { ok: false, feil: error.message };
}

/**
 * Invitasjoner som ble loest inn ved siste innlogging.
 *
 * En modulvariabel og ikke en tilstand i React: den settes dypt inne i
 * oppbyggingen av oekten, og leses av konsollen naar den tegnes. Et
 * tall som skal overleve én navigasjon og ikke mer.
 */
let nyeInvitasjoner = 0;
let sisteInvitasjonsfeil: string | null = null;

export function lesNyeInvitasjoner(): number {
  const n = nyeInvitasjoner;
  nyeInvitasjoner = 0;
  return n;
}

/**
 * Gikk innloesingen galt ved siste innlogging?
 *
 * Konsollen viser den. En invitasjon som ikke bandt seg ser ut som ingen
 * invitasjon, og den som venter paa aa komme inn i en produksjon har
 * ingen maate aa skille de to paa.
 */
export function lesInvitasjonsfeil(): string | null {
  const f = sisteInvitasjonsfeil;
  sisteInvitasjonsfeil = null;
  return f;
}

export async function loggUt(): Promise<void> {
  /* Valget ryddes foerst. Ellers ville neste innlogging — kanskje en
     annen person paa samme maskin i et kontrollrom — landet rett i
     produksjonen den forrige sto i. */
  velgProduksjon(null);
  await supabase?.auth.signOut();
}

interface Medlemsrad {
  readonly id: string;
  readonly role: Rolle;
  readonly extra_roles: readonly Rolle[] | null;
  readonly display_name: string;
  readonly initials: string;
  readonly unit_id: string | null;
  readonly unit: { readonly name: string } | null;
  readonly craft: { readonly name: string } | null;
  readonly production: {
    readonly id: string;
    readonly name: string;
    readonly framerate: string;
    readonly drop_frame: boolean;
    readonly end_date: string;
    readonly nle: string | null;
    readonly logo_path: string | null;
    readonly company: { readonly logo_path: string | null } | null;
  } | null;
}

/**
 * Opptaksdagen produksjonen står i.
 *
 * LESES, REGNES IKKE. Nummer og dato følger ikke hverandre av seg selv — en
 * dag kan utgå uten at nummereringen forskyves. Er det ingen dag i dag, tas
 * den siste som har vært: man jobber ofte med gårsdagen dagen derpå.
 */
async function dagenNaa(produksjonId: string): Promise<string | null> {
  if (supabase === null) return null;
  /*
   * ÉN REGEL, ETT STED. `dagen_naa` i basen svarer det samme til appen
   * og hit: vinduet foerst — et nattskift fra 18:00 til 03:30 er én
   * opptaksdag, og klokka 01:00 er man fortsatt i den — saa dagens
   * dato, saa den siste som har begynt.
   *
   * Her sto spoerringen for haand, med bare datoen. Den var riktig helt
   * til en opptaksdag gikk over midnatt. Se migrasjon 20261102110000.
   */
  const { data, error } = await supabase.rpc(
    "dagen_naa", { p_produksjon: produksjonId });
  if (error !== null || data == null) return null;
  return String(data);
}

/**
 * Økten, bygd av medlemskapet til den som er logget inn.
 *
 * Returnerer null når ingen er logget inn, eller når den innloggede ikke er
 * medlem noe sted. De to er ulike tilstander for flata, og den skiller på
 * dem — se `InnloggingScreen`.
 */
export async function hentOektFraServer(
  valgt: string | null = null,
): Promise<Oekt | null> {
  if (supabase === null) return null;
  const { data: auth } = await supabase.auth.getUser();
  if (auth.user == null) return null;

  /*
   * INVITASJONENE KREVES FOERST.
   *
   * Prodleder har lagt inn adressen din under oppsettet, og raden staar
   * inert til noen binder den til en konto. Gjoer vi det etter at oekten
   * er bygd, moeter en ny redaktoer «Du er ikke med i en produksjon» paa
   * foerste innlogging — og maa laste siden om for aa komme inn.
   *
   * Adressen leses av tjeneren fra tokenet, ikke sendt herfra. Se
   * migrasjon 20261027180000.
   */
  const { data: krevd, error: krevfeil } = await supabase.rpc("krev_invitasjoner");
  /*
   * FEILEN SKAL IKKE KASTES, og det ble den.
   *
   * Her sto bare `await supabase.rpc(...)`. Funksjonen laa i
   * `tilgang`-skjemaet, som ikke er eksponert, saa hvert eneste kall
   * svarte PGRST202 «Could not find the function» — i fire dager, paa
   * begge flater. Invitasjonene bandt seg aldri, og ingenting sa fra:
   * den inviterte saa bare en tom liste.
   *
   * Nullstillingen av `nyeInvitasjoner` skjer ikke her; konsollen leser
   * og tømmer. Feilen gaar samme vei.
   */
  if (krevfeil !== null) {
    sisteInvitasjonsfeil = krevfeil.message;
    console.warn("[infoflyt] krev_invitasjoner feilet:", krevfeil.message);
  } else if (typeof krevd === "number" && krevd > 0) {
    nyeInvitasjoner += krevd;
  }

  /*
   * ÉN BESTEMT PRODUKSJON, IKKE «den foerste».
   *
   * Her sto `.limit(1)` uten noe valg, og da fantes bare den produksjonen
   * serveren tilfeldigvis ga foerst. En frilanser paa tre jobber saa én av
   * dem og kunne ikke komme til de andre — samme feil som feltappen hadde
   * og som ble rettet der. Konsollen velger, og valget kommer inn hit.
   *
   * Uten et valg returneres null, og flata viser konsollen. Det er ikke en
   * feiltilstand: det er landingen.
   */
  if (valgt === null) return null;

  const { data, error } = await supabase
    .from("production_member")
    .select(
      "id, role, extra_roles, display_name, initials, unit_id,"
      + " unit:unit_id(name), craft:craft_id(name),"
      + " production:production(id, name, framerate, drop_frame, end_date, nle,"
      + " logo_path, company:company(logo_path))",
    )
    .eq("user_id", auth.user.id)
    .eq("production_id", valgt)
    .maybeSingle();
  if (error !== null || data === null) return null;

  const m = data as unknown as Medlemsrad;
  const p = m.production;
  if (p === null) return null;

  const dag = await dagenNaa(p.id);

  return {
    production: {
      id: p.id,
      framerate: p.framerate as Oekt["production"]["framerate"],
      dropFrame: p.drop_frame,
      name: p.name,
      endDateIso: p.end_date,
      nle: p.nle as Oekt["production"]["nle"],
      /* Regelen brukes HER, én gang. Se Oekt i state/session.ts. */
      logoSti: velgLogo(p.logo_path, p.company?.logo_path),
    },
    member: {
      id: m.id,
      displayName: m.display_name,
      initials: m.initials,
      role: m.role,
      extraRoles: m.extra_roles ?? [],
      unitId: m.unit_id,
      unitName: m.unit?.name ?? "",
      craftNavn: m.craft?.name ?? "",
    },
    shootDay: dag ?? "1",
  };
}

/**
 * Sier fra naar oekten endrer seg.
 *
 * Porten i `App.tsx` leser oekten én gang. Den maa vite naar koden er loest
 * inn — og naar noen logger ut i menyen — ellers staar flata paa kortet, eller
 * paa en produksjon man nettopp gikk ut av, til man laster siden om selv.
 */
export function paaEndring(f: () => void): () => void {
  if (supabase === null) return () => undefined;
  const { data } = supabase.auth.onAuthStateChange(() => { f(); });
  return () => { data.subscription.unsubscribe(); };
}

/**
 * Tar tokenet ut av adressefeltet.
 *
 * Innlogging sender KODE, saa normalt er det ingenting aa rydde. Men
 * e-postbytte og invitasjon kommer som lenke, og da staar tokenet som
 * `#access_token=…` i nettleserens historikk — paa en maskin som ofte deles i
 * et kontrollrom. Oekten er alt lagret naar vi kommer hit, saa fragmentet har
 * gjort sitt.
 *
 * `replaceState` og ikke `pushState`: tilbakeknappen skal ikke foere tilbake
 * til en adresse med et token i.
 */
export function ryddAdressen(): void {
  if (!window.location.hash.includes("access_token")) return;
  window.history.replaceState({}, "", window.location.pathname);
}
